import React from 'react';
import { TableRow } from '@material-ui/core';
import { isNumber } from 'lodash/fp';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GetPaginatedProductStatisticsQuery } from '../../../../generated/graphql';
import { HoverLink, LoaderRow, TableCell } from '../../../../ui-kit';
import { useUser } from '../../../../utils/hooks/auth';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { routePaths } from '../../../../routePaths';
import { getHeadCells } from './utils';

interface TableContentProps {
    isLoading?: boolean;
    rowsPerPage: number;
    items: GetPaginatedProductStatisticsQuery['productStatistics']['items'];
}

const TableContent = ({ isLoading, rowsPerPage, items }: TableContentProps) => {
    const { user: { venueType, defaultCurrency: { currencyCode } } } = useUser();
    const headCells = getHeadCells(venueType);
    const { priceService } = usePriceService();
    const location = useLocation();
    const { t } = useTranslation();

    const rows = items.map(({
        id, productName, revenue, quantity, foodCost, markup, netRevenue, primeCost,
    }) => ({
        id,
        productName,
        revenue: priceService.toLocaleString(revenue, currencyCode),
        netRevenue: priceService.toLocaleString(netRevenue, currencyCode),
        quantity,
        foodCost: isNumber(foodCost) ? `${priceService.fractionToPercent(foodCost, 2)}%` : t('N/A'),
        markup: isNumber(markup) ? priceService.toLocaleString(markup, currencyCode) : t('N/A'),
        primeCost: isNumber(primeCost) ? priceService.toLocaleString(primeCost, currencyCode) : t('N/A'),
    }));

    return isLoading
        ? <LoaderRow columnCount={headCells.length} itemsPerPage={rowsPerPage} itemHeight={56} />
        : (
            <>
                {rows.map(row => (
                    <TableRow tabIndex={-1} key={row.id}>
                        {headCells.map(({ id, align }, index) => {
                            const cellContent = id === 'productName'
                                ? (
                                    <HoverLink
                                        wide
                                        to={{
                                            pathname:`${routePaths.products}/${row.id}`,
                                            state: { referrer: location },
                                        }}
                                    >
                                        {row[id]}
                                    </HoverLink>
                                )
                                : row[id];
                            return (
                                <TableCell key={`${row.id}-${id}`} isSticky={index === 0} align={align}>
                                    {cellContent}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                ))}
            </>
        );
};

export default TableContent;
